
import React from 'react';
import type { ContentStatus } from '../../types';
import { PlayArrowIcon, EditIcon, DeleteIcon } from '../icons';
import ProcessingStatusIndicator, { ProcessingStatus } from './ProcessingStatusIndicator';
import styles from './VideoCard.module.scss';

interface VideoCardProps {
    id: number;                     // Идентификатор видео
    title: string;                  // Заголовок видео
    description?: string;           // Описание видео
    thumbnailUrl: string;           // URL миниатюры
    videoUrl: string;               // URL видео
    status?: ContentStatus;         // Статус публикации
    publishDate?: string | null;    // Дата публикации
    processingStatus?: ProcessingStatus; // Статус обработки видео
    processingMessage?: string;     // Сообщение о процессе обработки
    onEdit: () => void;             // Обработчик редактирования
    onDelete: () => void;           // Обработчик удаления
    onClick: () => void;            // Обработчик клика по карточке
    hideTitle?: boolean;            // Флаг скрытия заголовка
    horizontal?: boolean;           // Флаг горизонтального отображения
    showProcessingIndicator?: boolean; // Показывать ли индикатор обработки
    isNewVideo?: boolean;           // Новое видео (показать полноэкранный лоадер)
}

const VideoCard: React.FC<VideoCardProps> = ({
    id,
    title,
    description,
    thumbnailUrl,
    videoUrl,
    processingStatus,
    processingMessage,
    onEdit,
    onDelete,
    onClick,
    hideTitle = false,
    horizontal = false,
    showProcessingIndicator = true,
    isNewVideo = false
}) => {

    const cardClass = horizontal ? styles.cardHorizontal : styles.card;
    const contentClass = horizontal ? styles.cardContentHorizontal : styles.cardContent;
    const titleClass = horizontal ? styles.cardTitleHorizontal : styles.cardTitle;
    const descriptionClass = horizontal ? styles.cardDescriptionHorizontal : styles.cardDescription;

    // Определяем, показывать ли полноэкранный лоадер для новых видео
    const showNewVideoLoader = isNewVideo && (!thumbnailUrl || !videoUrl);

    // Заглушка для изображения
    const placeholderImage = "data:image/svg+xml,%3Csvg width='320' height='180' xmlns='http://www.w3.org/2000/svg'%3E%3Crect width='100%25' height='100%25' fill='%23f0f0f0'/%3E%3Ctext x='50%25' y='50%25' dominant-baseline='middle' text-anchor='middle' font-family='Arial, sans-serif' font-size='14' fill='%23999'%3EЗагрузка...%3C/text%3E%3C/svg%3E";

    const imageSrc = thumbnailUrl || placeholderImage;

    return (
        <div className={cardClass}>
            <div className={styles.cardMedia} onClick={onClick}>
                <img
                    src={imageSrc}
                    alt={title}
                    className={styles.cardMediaImg}
                />
                <div className={styles.playIcon}>
                    <PlayArrowIcon />
                </div>
                {/* Показываем индикатор обработки только если разрешено и статус не completed */}
                {showProcessingIndicator && processingStatus && processingStatus !== 'completed' && (
                    <div className={styles.processingOverlay}>
                        <ProcessingStatusIndicator
                            status={processingStatus}
                            message={processingMessage}
                            size="small"
                            showMessage={false}
                        />
                    </div>
                )}

                {/* Полноэкранный лоадер для новых видео */}
                {showNewVideoLoader && (
                    <div className={styles.newVideoLoader}>
                        <div className={styles.newVideoLoaderContent}>
                            <div className={styles.newVideoSpinner}></div>
                            <p className={styles.newVideoText}>Обработка видео...</p>
                        </div>
                    </div>
                )}
            </div>
            {!hideTitle && (title || description) && (
                <div className={contentClass}>
                    {title && <h3 className={titleClass}>{title}</h3>}
                    {description && <p className={descriptionClass}>{description}</p>}
                </div>
            )}
            <div className={styles.cardActions}>
                <button
                    className={styles.actionButton}
                    onClick={onEdit}
                    title="Edit"
                >
                    <EditIcon />
                </button>
                <button
                    className={styles.actionButton}
                    onClick={onDelete}
                    title="Delete"
                >
                    <DeleteIcon />
                </button>
            </div>
        </div>
    );
};

export default VideoCard;